import React, { useState, useCallback, memo, useMemo, Suspense, useRef, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Avatar, Badge, IconButton, Link, useTheme, Box, Typography, Tooltip, alpha, Dialog, DialogContent, CircularProgress } from '@mui/material';
import { AnalyticsOutlined, CalendarMonthOutlined, ExploreOutlined, GridViewOutlined, NotificationsOutlined, PieChartOutline, InfoOutlined, Close as CloseIcon } from '@mui/icons-material';
import Stack from 'components/mui/Stack';
import AppBoxComponent from 'components/mui/AppBoxComponent';
import NavigationTabsComponent from 'components/layouts/NavigationTabsComponent';
import ProfileMenuComponent from 'components/layouts/ProfileMenuComponent';
import { getInitials } from 'utils/string.utils';
import ROUTES from 'router/routes.constants';
import Logo from 'assets/images/logo.svg';
import { LoadingFallback } from 'components/common';
import exploreBannerVideo from 'assets/video/exploreBanner.mp4';
import AppButtonComponent from 'components/mui/AppButtonComponent';
import ServiceRequestContainer from 'containers/ServiceRequestContainer';
import ApiHistoryDrawer from 'components/common/ApiHistoryDrawer';
import BreadcrumbNavigation from 'components/common/BreadcrumbNavigation';
import { useGetNotificationCountsQuery } from 'store/api/notifications.apislice';
import { useGetConsentsQuery } from 'store/api/finfactor.apislice';
import PullToRefreshComponent from 'components/mobile/PullToRefreshComponent';
import { baseApi } from 'store/api/baseApi';
import { useDispatch } from 'react-redux';
import { isMobile, isNative, isWeb } from 'utils/platform.utils';
import { useGetOnboardingStatusQuery } from 'store/api/customer.apislice';
import { useUpdateCustomerTypeMutation } from 'store/api/mutualFunds.apislice';
import ChooseExperienceModal from 'components/ChooseExperienceModal';
import toast from 'react-hot-toast';
import { useSubscriptionPayment } from 'hooks/useSubscriptionPayment';
import DigioAgreementModal from 'components/DigioAgreementModal';
import { useGetCurrentSubscriptionQuery } from 'store/api/subscription.apislice';

// Component scope - Constants and static data
const NAV_TABS = [
  { label: 'Home', href: ROUTES.EXPLORE, icon: <ExploreOutlined /> },
  { label: 'My Investments', href: ROUTES.INVESTMENTS, icon: <GridViewOutlined /> },
  { label: 'Explore Mutual Funds', href: ROUTES.MUTUAL_FUNDS, icon: <PieChartOutline /> },
  { label: 'My Portfolio', href: ROUTES.PORTFOLIO, icon: <AnalyticsOutlined /> },
];

// Route groups for active tab detection
const ROUTE_GROUPS = [
  {
    tabIndex: 0, // Explore tab
    routes: [ROUTES.EXPLORE, '/'],
  },
  {
    tabIndex: 1, // Investments tab
    routes: [
      ROUTES.INVESTMENTS,
      ROUTES.FINANCIAL_OVERVIEW,
      ROUTES.INVESTMENT_PORTFOLIO,
      ROUTES.INVESTMENT_PORTFOLIO_DETAILS,
      ROUTES.INVESTMENT_PORTFOLIO_BROKER,
      ROUTES.INVESTMENT_PORTFOLIO_ORDER,
      ROUTES.FINFACTOR_BANKING,
      ROUTES.FINFACTOR_BANK_DETAILS,
      ROUTES.FINFACTOR_INSURANCE,
    ],
    matchPrefix: '/financial-overview', // Matches any /financial-overview/* route
  },
  {
    tabIndex: 2, // MF Universe tab
    routes: [
      ROUTES.MUTUAL_FUNDS,
      ROUTES.MUTUAL_FUNDS_FUND_ID,
    ],
    matchPrefix: '/mutual-funds', // Matches any /mutual-funds/* route
  },
  {
    tabIndex: 3, // Portfolio tab
    routes: [
      ROUTES.PORTFOLIO,
      ROUTES.PORTFOLIO_MUTUAL_FUND,
      ROUTES.PORTFOLIO_MUTUAL_FUND_FUND_ID,
      ROUTES.PORTFOLIO_ORDER,
      ROUTES.PORTFOLIO_ORDER_ORDER_ID,
      ROUTES.PORTFOLIO_SUMMARY,
      ROUTES.SYSTEMATIC_INVESTMENT,
      ROUTES.SIP_ROOT,
      ROUTES.SIP_ACTIVE,
      ROUTES.STP_ROOT,
      ROUTES.STP_ACTIVE,
      ROUTES.SWP_ROOT,
      ROUTES.SWP_ACTIVE,
      ROUTES.SIPS_ACTIVE,
      ROUTES.SIPS_MANDATES,
      ROUTES.SIPS_SUMMARY,
      ROUTES.SIPS_SIP_ID,
    ],
    matchPrefix: ['/portfolio', '/systematic-investment'], // Matches both prefixes
  },
];
// const ROUTE_GROUPS = [
//   {
//     tabIndex: 0, // Explore tab
//     routes: [ROUTES.EXPLORE, '/'],
//   },
//   {
//     tabIndex: 1, // Investments tab
//     routes: [ROUTES.INVESTMENTS],
//   },
//   {
//     tabIndex: 2, // MF Universe tab
//     routes: [
//       ROUTES.MUTUAL_FUNDS,
//       ROUTES.MUTUAL_FUNDS_FUND_ID,
//     ],
//     matchPrefix: '/mutual-funds', // Matches any /mutual-funds/* route
//   },
//   {
//     tabIndex: 3, // Portfolio tab
//     routes: [
//       ROUTES.PORTFOLIO,
//       ROUTES.PORTFOLIO_MUTUAL_FUND,
//       ROUTES.PORTFOLIO_MUTUAL_FUND_FUND_ID,
//       ROUTES.PORTFOLIO_ORDER,
//       ROUTES.PORTFOLIO_ORDER_ORDER_ID,
//       ROUTES.PORTFOLIO_SUMMARY,
//     ],
//     matchPrefix: '/portfolio', // Matches any /portfolio/* route
//   },
//   {
//     tabIndex: 4, // SIPs tab
//     routes: [
//       ROUTES.SIPS,
//       ROUTES.SIPS_ACTIVE,
//       ROUTES.SIPS_SUMMARY,
//       ROUTES.SIPS_SIP_ID,
//     ],
//     matchPrefix: '/sips', // Matches any /sips/* route
//   },
// ];

const LAYOUT_STYLES = {
  container: { minHeight: '100vh' },
  header: {
    flex: '0 0 auto',
    width: 1,
    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
    backgroundColor: (theme) => theme.palette.custom?.background?.page || '#13172A', // Ensure opaque background
    // paddingTop: 'env(safe-area-inset-top, 0px)', // Handled globally by AppHOC
  },
  headerContent: {
    maxWidth: '1300px',
    width: 1,
    margin: '0 auto',
    // For xs: stack vertically, for sm+: horizontal
    flexDirection: { xs: 'column', sm: 'row' },
    alignItems: { xs: 'stretch', sm: 'center' },
  },
  topRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: 1,
    px: { xs: 1.5, sm: 2 },
    py: { xs: 1.5, sm: 1 },
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: { xs: 80, sm: 100 },
    height: { xs: 38, sm: 42 },
    cursor: 'pointer',
    transition: 'opacity 0.2s ease-in-out',
    '&:hover': {
      opacity: 0.8,
    },
    '& img': {
      maxHeight: { xs: '32px', sm: '36px' },
      width: 'auto',
      objectFit: 'contain',
      display: 'block',
      marginTop: '1px' // Precise shift to align baseline with tab text
    }
  },
  spacer: { flex: '1 1 auto' },
  navTabsContainer: {
    width: 1,
    // Show stacked/scrollable for xs/sm (up to md), then hide
    display: { xs: 'block', md: 'none' },
  },
  navTabsInline: {
    // Show inline with logo from md+ screens
    display: { xs: 'none', md: 'flex' },
    flex: '1 1 auto',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  mainContent: {
    flex: '1 1 auto',
    width: 1,
    maxWidth: '1300px',
    margin: '0 auto',
    // paddingBottom: 'env(safe-area-inset-bottom, 0px)', // Handled globally by AppHOC
  },
  exploreContentWrapper: {
    position: 'relative',
    flex: '1 1 auto',
    width: 1,
    maxWidth: '100%',
    margin: 0,
    overflow: 'hidden',
    backgroundColor: (theme) => theme.palette.custom?.background?.page || '#13172A',
  },
  exploreBackgroundVideo: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    zIndex: 0,
    filter: 'brightness(0.65)', display: 'block',
  },
  exploreContentOverlay: {
    position: 'relative',
    zIndex: 1,
    flex: '1 1 auto',
    width: 1,
    maxWidth: '1300px',
    margin: '0 auto',
    px: { xs: 1.5, sm: 2 },
    pt: { xs: 3, sm: 4 },
    pb: { xs: '24px', sm: '32px' }, // Modified to remove double safe area
  },
};

/**
 * Main Layout Component - Main application layout with navigation and profile menu
 * @component
 * @returns {React.ReactElement} MainLayout
 */
const MainLayout = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // States, refs, selectors, RTK Query hooks
  // States, refs, selectors, RTK Query hooks
  const { data: countsData, refetch: refetchNotifications } = useGetNotificationCountsQuery(undefined, {
    // NOTE: refetchOnMountOrArgChange only triggers on mount or argument change.
    // It does NOT respond to tag invalidation automatically if the component is already mounted.
    // Tag invalidation (invalidateTags) is the correct mechanism for Pull-to-Refresh.
    refetchOnMountOrArgChange: true,
  });
  const { data: consentsData, refetch: refetchConsents } = useGetConsentsQuery();

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [showChooseExperience, setShowChooseExperience] = useState(false);
  const [showAgreement, setShowAgreement] = useState(false);
  const [showOnboardingRequired, setShowOnboardingRequired] = useState(false);
  const hasAgreementBeenDismissed = useRef(false);
  const hasClosedPlanModal = useRef(false);
  const user = useSelector(state => state.user?.profile);

  // Global Onboarding checking
  const { data: onboardingData, isLoading: isLoadingOnboarding, refetch: refetchOnboarding } = useGetOnboardingStatusQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const [updateCustomerType] = useUpdateCustomerTypeMutation();
  const { data: subscriptionResponse, isLoading: isSubLoading, isFetching: isSubFetching } = useGetCurrentSubscriptionQuery();
  const customerType = onboardingData?.data?.customerType || onboardingData?.data?.customer_type;
  const isSubscriptionActive =
    subscriptionResponse?.result?.status === 'ACTIVE' ||
    subscriptionResponse?.result?.current?.status === 'ACTIVE' ||
    customerType === 'REGULAR' ||
    onboardingData?.data?.isSubscriptionFree === true ||
    onboardingData?.onboardingStatus?.isSubscriptionFree === true;

  const {
    initiatePayment,
    isVerifying,
    verificationMessage,
    cancelVerification,
    paymentUrl
  } = useSubscriptionPayment();

  // Watch onboarding status and force show ChooseExperienceModal or other flow modals
  useEffect(() => {
    if (isLoadingOnboarding || isSubLoading || isVerifying || paymentUrl || !onboardingData) return;

    const customerType = onboardingData?.data?.customerType || onboardingData?.data?.customer_type;
    const preOnboardingDirect = sessionStorage.getItem("selectedPlanPreOnboarding") === "DIRECT";
    const isDirect = customerType === "DIRECT" || (!customerType && preOnboardingDirect);

    const isOnboardingCompleted =
      onboardingData?.isOnboardingCompleted === true ||
      onboardingData?.data?.isOnboardingCompleted === true;

    const onboardingDone = isOnboardingCompleted;

    const isMutualFundsRoute = location.pathname.startsWith("/mutual-funds");

    if (!customerType) {
      if (!onboardingDone) {
        // If onboarding is NOT done: show experience selector only if they haven't selected Direct pre-onboarding
        if (!preOnboardingDirect) {
          const hasClosed = hasClosedPlanModal.current;
          if (!hasClosed || isMutualFundsRoute) {
            setShowChooseExperience(true);
          }
        } else {
          setShowChooseExperience(false);
        }
        setShowOnboardingRequired(false);
        setShowAgreement(false);
        hasAgreementBeenDismissed.current = false;
      } else {
        // Onboarding IS completed, but customerType is null: show ChooseExperienceModal again
        const hasClosed = hasClosedPlanModal.current;
        if (!hasClosed || isMutualFundsRoute) {
          setShowChooseExperience(true);
        }
        setShowAgreement(false);
      }
    } else {
      if (isDirect) {
        if (!onboardingDone) {
          setShowChooseExperience(false);
          setShowAgreement(false);
        } else {
          // Onboarding completed, but payment is not done: show ChooseExperienceModal again!
          if (!isSubscriptionActive) {
            const hasClosed = hasClosedPlanModal.current;
            if (!hasClosed || isMutualFundsRoute) {
              setShowChooseExperience(true);
            }
            setShowAgreement(false);
          } else {
            setShowChooseExperience(false);
            // If subscription is paid, and agreement is not completed: show e-sign automatically
            const agreementCompleted = onboardingData?.data?.agreement?.completed;
            if (!agreementCompleted && !hasAgreementBeenDismissed.current) {
              setShowAgreement(true);
            } else {
              setShowAgreement(false);
            }
          }
        }
      } else {
        setShowChooseExperience(false);
        setShowAgreement(false);
      }
    }
  }, [onboardingData, isLoadingOnboarding, isSubLoading, isVerifying, paymentUrl, isSubscriptionActive, location.pathname]);

  // Global click interceptor to automatically reopen plan selection modal when clicking anything in the product
  useEffect(() => {
    const handleGlobalClick = (e) => {
      if (isLoadingOnboarding || isSubLoading || isSubFetching || isVerifying || paymentUrl || !onboardingData) return;

      const customerType = onboardingData?.data?.customerType || onboardingData?.data?.customer_type;
      const preOnboardingDirect = sessionStorage.getItem("selectedPlanPreOnboarding") === "DIRECT";
      const isDirect = customerType === "DIRECT" || (!customerType && preOnboardingDirect);

      const isOnboardingCompleted =
        onboardingData?.isOnboardingCompleted === true ||
        onboardingData?.data?.isOnboardingCompleted === true;

      const onboardingDone = isOnboardingCompleted;

      // Reopen plan selection if either:
      // 1. They haven't selected anything yet (customerType is null and selectedPlanPreOnboarding is not DIRECT)
      // 2. OR they have DIRECT pre-onboarding / post-onboarding and onboarding is complete, but they haven't paid
      const shouldEnforcePlanSelection =
        (!customerType && !preOnboardingDirect) ||
        (isDirect && onboardingDone && !isSubscriptionActive);

      if (shouldEnforcePlanSelection) {
        // If clicking anywhere outside the dialog/modals, open the plan selection modal
        const isClickInsideModal = e.target.closest('.MuiDialog-root');
        if (!isClickInsideModal) {
          setShowChooseExperience(true);
        }
      }
    };

    window.addEventListener('click', handleGlobalClick, true); // use capture phase
    return () => {
      window.removeEventListener('click', handleGlobalClick, true);
    };
  }, [onboardingData, isLoadingOnboarding, isSubscriptionActive]);

  // Background polling of onboarding status commented out to prevent unnecessary API load/crashing
  // Polling is handled specifically in DigioAgreementModal when the signing flow is active.
  /*
  useEffect(() => {
    if (isLoadingOnboarding || !onboardingData) return;

    const customerType = onboardingData?.data?.customerType || onboardingData?.data?.customer_type;
    const isDirect = customerType === "DIRECT";
    const agreementCompleted = onboardingData?.data?.agreement?.completed;

    let intervalId;
    if (isDirect && !agreementCompleted) {
      intervalId = setInterval(() => {
        refetchOnboarding();
      }, 5000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [onboardingData, isLoadingOnboarding, refetchOnboarding]);
  */

  // Listen to manual open modal events from click triggers
  useEffect(() => {
    const handleOpenAgreementModal = () => {
      setShowAgreement(true);
    };
    const handleOpenPlanModal = () => {
      setShowChooseExperience(true);
    };
    window.addEventListener('open-agreement-modal', handleOpenAgreementModal);
    window.addEventListener('open-plan-selection-modal', handleOpenPlanModal);
    return () => {
      window.removeEventListener('open-agreement-modal', handleOpenAgreementModal);
      window.removeEventListener('open-plan-selection-modal', handleOpenPlanModal);
    };
  }, []);

  const handleChooseExperienceContinue = async (selectedExperience) => {
    const isOnboardingCompleted =
      onboardingData?.isOnboardingCompleted === true ||
      onboardingData?.data?.isOnboardingCompleted === true;

    const onboardingDone = isOnboardingCompleted;

    if (selectedExperience === "fee-based") {
      try {
        setShowChooseExperience(false);
        if (onboardingDone) {
          // Onboarding completed, selecting Direct Plan:
          // 1. Call API to set customer type to DIRECT on backend
          await updateCustomerType({ customerType: "DIRECT" }).unwrap();
          if (refetchOnboarding) {
            await refetchOnboarding();
          }
          // 2. Start Razorpay payment checkout directly!
          await initiatePayment(1);
        } else {
          // Onboarding NOT completed, DO NOT call create API. Just save selection to sessionStorage and show prompt
          sessionStorage.setItem("selectedPlanPreOnboarding", "DIRECT");
          setShowOnboardingRequired(true);
        }
      } catch (err) {
        console.error("Failed to proceed with Direct Plan selection:", err);
      }
    } else {
      try {
        await updateCustomerType({ customerType: "REGULAR" }).unwrap();
        if (refetchOnboarding) {
          await refetchOnboarding();
        }
        setShowChooseExperience(false);
      } catch (err) {
        console.error("Failed to select Regular Plan:", err);
      }
    }
  };

  const handleChooseExperienceClose = () => {
    setShowChooseExperience(false);
    hasClosedPlanModal.current = true;

    const isMutualFundsRoute = location.pathname.startsWith("/mutual-funds");

    if (isMutualFundsRoute) {
      navigate(ROUTES.EXPLORE, { replace: true });
    }
  };

  // Watch onboarding status and block explore funds page for Direct plan users who haven't completed onboarding and payment
  useEffect(() => {
    if (isLoadingOnboarding || !onboardingData) return;

    const customerType = onboardingData?.data?.customerType || onboardingData?.data?.customer_type;
    const preOnboardingDirect = sessionStorage.getItem("selectedPlanPreOnboarding") === "DIRECT";
    const isDirect = customerType === "DIRECT" || (!customerType && preOnboardingDirect);

    const isOnboardingCompleted =
      onboardingData?.isOnboardingCompleted === true ||
      onboardingData?.data?.isOnboardingCompleted === true;

    const isUCCOnboardingCompleted =
      onboardingData?.isUCCOnboardingCompleted === true ||
      onboardingData?.data?.isUCCOnboardingCompleted === true;

    const onboardingDone = isOnboardingCompleted && isUCCOnboardingCompleted;

    const isExploreRoute =
      location.pathname === "/explore" ||
      location.pathname.startsWith("/explore/mf-collection-details") ||
      location.pathname.startsWith("/mutual-funds");

    if (isDirect) {
      if (!onboardingDone && isExploreRoute) {
        navigate(ROUTES.ONBOARDING_STATUS, { replace: true });
        toast.error("Please complete your onboarding first to explore Direct Funds.");
      } else if (onboardingDone && !isSubscriptionActive && location.pathname.startsWith("/mutual-funds")) {
        navigate(ROUTES.EXPLORE, { replace: true });
      }
    }
  }, [location.pathname, onboardingData, isLoadingOnboarding, isSubscriptionActive, navigate]);

  const open = Boolean(menuAnchorEl);
  const userName = user?.fullName || user?.name || 'User';
  const userEmail = user?.email || 'user@example.com';

  const unreadCount = countsData?.counts?.unread || 0;

  // Active tab detection logic - simplified without useCallback
  const getActiveTabIndex = (pathname) => {
    for (const group of ROUTE_GROUPS) {
      // Check exact matches first
      if (group.routes.some(route => {
        // Handle dynamic routes with parameters (e.g., /mutual-funds/:fundId)
        if (route.includes(':')) {
          const pattern = route.replace(/:[^/]+/g, '[^/]+');
          const regex = new RegExp(`^${pattern}$`);
          return regex.test(pathname);
        }
        return pathname === route;
      })) {
        return group.tabIndex;
      }

      // Check prefix match if defined
      if (group.matchPrefix) {
        const prefixes = Array.isArray(group.matchPrefix) ? group.matchPrefix : [group.matchPrefix];
        if (prefixes.some(prefix => pathname.startsWith(prefix))) {
          return group.tabIndex;
        }
      }
    }

    // No tab selected for other routes (onboarding, profile, etc.)
    return false;
  };

  // Compute active tab value directly - no state needed
  const tabValue = useMemo(() => {
    return getActiveTabIndex(location.pathname);
  }, [location.pathname]);

  // Determine if current route should use full-width content (no side spacing)
  const isFullWidthContent = useMemo(() => {
    return location.pathname.startsWith('/mutual-funds');
  }, [location.pathname]);

  const shouldShowExploreBanner = useMemo(() => {
    return location.pathname === ROUTES.EXPLORE;
  }, [location.pathname]);

  const shouldShowServiceRequest = useMemo(() => {
    return !location.pathname.includes('mutual-funds') &&
      !location.pathname.includes('risk-assessment') &&
      !location.pathname.includes('invest') &&
      !location.pathname.includes('portfolio') &&
      !location.pathname.includes('reports');
  }, [location.pathname]);

  const breadcrumbPadding = useMemo(() => {
    // Universe page (where container has md: 4)
    if (location.pathname.startsWith('/mutual-funds')) {
      return { xs: 2, sm: 4, md: 7 }; // Container(1, 3, 4) + Table(1, 1, 3)
    }
    // Collection details page (where container has md: 0)
    if (location.pathname.includes('/explore/mf-collection-details/')) {
      return { xs: 2, sm: 4, md: 3 }; // Container(1, 3, 0) + Table(1, 1, 3)
    }
    return { xs: 1.5, sm: 2 };
  }, [location.pathname]);

  const showReturnLegend = useMemo(() => {
    return location.pathname === '/mutual-funds' ||
      location.pathname.includes('/explore/mf-collection-details/');
  }, [location.pathname]);

  const ReturnLegend = () => {
    const legends = [
      { label: 'Poor', color: '#FF4D4D' },
      { label: 'Fair', color: '#FF9433' },
      { label: 'Good', color: '#FFD918' },
      { label: 'Very Good', color: '#99E63E' },
      { label: 'Excellent', color: '#00BE57' },
    ];

    return (
      <Box sx={{
        display: { xs: 'none', md: 'flex' },
        alignItems: 'center',
        gap: 2,
        backgroundColor: '#1C2033',
        border: `1px solid ${theme.palette.custom?.stroke?.divider || alpha('#fff', 0.12)}`,
        borderRadius: 2,
        p: 0.5,
        px: 1.5,
      }}>
        <Tooltip
          title={
            <Box>
              <ul style={{
                margin: 0,
                paddingLeft: '16px',
                fontSize: '13px',
                fontFamily: theme.typography?.fontFamily || 'inherit',
                fontWeight: 400,
                lineHeight: 1.5,
                color: '#E5E7EB'
              }}>
                <li style={{ paddingBottom: '6px' }}>Funds are bucketed on various parameters with respect to other funds in their category.</li>
                <li>Annualized returns are displayed for 1 year and above.</li>
              </ul>
            </Box>
          }
          placement="left"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: '#2E3545',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                borderRadius: '8px',
                // p: '12px 16px',
                maxWidth: '600px',
                boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
                '& .MuiTooltip-arrow': {
                  color: '#2E3545',
                  "&::before": {
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                  }
                },
              },
            },
          }}
        >
          <InfoOutlined sx={{ fontSize: 18, color: theme.palette.custom?.text?.helperEnabled || '#9CA3AF', cursor: 'pointer' }} />
        </Tooltip>

        {legends.map((item) => (
          <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{
              width: 12,
              height: 12,
              borderRadius: '2px',
              backgroundColor: item.color,
              flexShrink: 0
            }} />
            <Typography sx={{
              fontSize: '11px',
              fontWeight: 500,
              color: theme.palette.custom?.text?.helperEnabled || '#9CA3AF',
              whiteSpace: 'nowrap'
            }}>
              {item.label}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  // Header scroll detection logic - simplified to always fixed
  const [headerHeight, setHeaderHeight] = useState(0);
  const headerRef = useRef(null);

  // Handle header offset for sticky sub-tabs
  useEffect(() => {
    // Always provide the offset when the header is fixed
    const offset = headerHeight;
    document.documentElement.style.setProperty('--header-offset', `${offset}px`);

    return () => {
      document.documentElement.style.removeProperty('--header-offset');
    };
  }, [headerHeight]);

  // Measure header height for spacer
  useEffect(() => {
    const updateHeight = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
    };

    // Initial measure
    updateHeight();

    // Measure on resize
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, [tabValue]); // Re-measure if tabs change (might affect height on mobile)

  // Other functions and logic - memoized with useCallback

  const handleMenuOpen = useCallback((event) => {
    setMenuAnchorEl(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
  }, []);

  const handleMenuItemClick = useCallback((itemId) => {
    console.log('Menu item clicked:', itemId);
    // Handle menu item actions here
  }, []);

  const handleNotificationsClick = useCallback(() => {
    navigate(ROUTES.NOTIFICATIONS);
  }, [navigate]);

  const handleRefresh = useCallback(async () => {
    console.log('🔄 Pull-To-Refresh: Triggered');

    try {
      // 1. Invalidate all major tags to trigger automatic refetch of active queries.
      // This is the primary mechanism for RTK Query to refresh data across the app.
      //  console.log('🔄 Pull-To-Refresh: Invalidating tags...');
      dispatch(baseApi.util.invalidateTags([
        'Portfolio',
        'InvestmentPortfolio',
        'Notifications',
        'Dashboard',
        'Consents',
        'SIPs',
        'MutualFunds',
        'BankingAccounts',
        'InsurancePolicies'
      ]));

      // 2. Manual refetch fallback for critical queries mounted in this layout.
      // NOTE: refetchOnMountOrArgChange: true does NOT trigger on tag invalidation.
      // We manually call refetch() here to guarantee these core UI elements refresh immediately.
      console.log('🔄 Pull-To-Refresh: Executing manual refetch fallback for layout queries...');
      await Promise.all([
        refetchNotifications().unwrap().catch(err => console.error('Notifications refetch failed:', err)),
        refetchConsents().unwrap().catch(err => console.error('Consents refetch failed:', err))
      ]);

      ////   console.log('✅ Pull-To-Refresh: Data refresh complete');
    } catch (error) {
      console.error('❌ Pull-To-Refresh: Error during refresh:', error);
    }

    // Add a small UX delay so the user sees the 'Refreshing' state for a moment
    await new Promise(resolve => setTimeout(resolve, 800));
  }, [dispatch, refetchNotifications, refetchConsents]);


  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTo(0, 0);
    document.body.scrollTo(0, 0);
    const root = document.getElementById('root');
    if (root) {
      root.scrollTo(0, 0);
    }
    const appScrollContainer = document.getElementById('app-scroll-container');
    if (appScrollContainer) {
      appScrollContainer.scrollTo(0, 0);
    }
  }, [location.pathname]);

  const handleTabChange = useCallback((event, newValue) => {
    // Tab navigation is handled by Link components, but we need this for controlled Tabs
    // The actual navigation happens via the href prop in AppLinkTabComponent
  }, []);

  // Return/rendering
  return (
    <Stack sx={LAYOUT_STYLES.container}>
      {/* Header */}
      {/* Header - Fixed at top */}
      <Stack
        ref={headerRef}
        sx={{
          ...LAYOUT_STYLES.header,
          position: 'sticky',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1100,
          transition: 'box-shadow 0.3s',
          boxShadow: 4, // Always show shadow since it's sticky
        }}
      >
        <Stack sx={LAYOUT_STYLES.headerContent}>
          {/* Top Row: Logo and Actions (for xs) / Full Row (for sm+) */}
          <Stack direction="row" alignItems="center" sx={LAYOUT_STYLES.topRow}>
            {/* Logo */}
            <AppBoxComponent display="flex" sx={LAYOUT_STYLES.logo} onClick={() => navigate(ROUTES.EXPLORE)}>
              <img src={Logo} alt="logo" />
            </AppBoxComponent>

            {/* Navigation Tabs - Inline for md+ screens */}
            <AppBoxComponent sx={LAYOUT_STYLES.navTabsInline}>
              <NavigationTabsComponent
                value={tabValue}
                onChange={handleTabChange}
                tabs={NAV_TABS}
                ariaLabel="main navigation"
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  minHeight: 'auto',
                  '& .MuiTabs-indicator': {
                    height: 2,
                  },
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    minWidth: { xs: 'auto', md: 72 },
                    paddingX: { xs: 1.5, md: 2 },
                  },
                }}
              />
            </AppBoxComponent>

            <AppBoxComponent sx={LAYOUT_STYLES.spacer} />

            {/* Right side actions */}
            <Stack direction="row" alignItems="center" spacing={1}>
              {/* Notifications */}
              <IconButton
                onClick={handleNotificationsClick}
                aria-label="notifications"
                size="small"
                sx={{
                  color: theme.palette.common.white,
                }}
              >
                <Badge
                  badgeContent={unreadCount}
                  color="error"
                  sx={{
                    '& .MuiBadge-badge': {
                      color: unreadCount > 0 ? theme.palette.common.white : 'transparent',
                      backgroundColor: unreadCount > 0 ? theme.palette.error.main : 'transparent',
                      fontSize: '0.65rem',
                      minWidth: '16px',
                      height: '16px',
                      padding: '0 4px',
                    }
                  }}
                >
                  <NotificationsOutlined fontSize="medium" />
                </Badge>
              </IconButton>

              {/* Profile Avatar */}
              <IconButton
                onClick={handleMenuOpen}
                aria-controls={open ? 'account-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                aria-label="user account menu"
                size="small"
                sx={{
                  p: 0.5,
                }}
              >
                <Avatar
                  children={getInitials(userName)}
                  variant="circular"
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.background.paper,
                    width: { xs: 32, sm: 36 },
                    height: { xs: 32, sm: 36 },
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  }}
                />
              </IconButton>

              {/* Profile Menu */}
              <ProfileMenuComponent
                userName={userName}
                userEmail={user?.email}
                anchorEl={menuAnchorEl}
                open={open}
                onClose={handleMenuClose}
                onMenuItemClick={handleMenuItemClick}
              />
            </Stack>
          </Stack>

          {/* Navigation Tabs - Below top row for xs screens */}
          <AppBoxComponent
            sx={{
              ...LAYOUT_STYLES.navTabsContainer,
              borderTop: (theme) => ({
                xs: `1px solid ${theme.palette.divider}`,
                sm: 'none',
              }),
            }}
          >
            <NavigationTabsComponent
              value={tabValue}
              onChange={handleTabChange}
              tabs={NAV_TABS}
              ariaLabel="main navigation"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                width: '100%',
                minHeight: 'auto',
                '& .MuiTabs-indicator': {
                  height: 2,
                },
                '& .MuiTab-root': {
                  minHeight: 48,
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  textTransform: 'none',
                  fontWeight: 400,
                  minWidth: { xs: '72px', sm: '120px' }, // Set a sensible min-width
                  paddingX: { xs: 2.5, sm: 3 }, // Increase padding for better touch targets
                  whiteSpace: 'nowrap',
                },
                '& .MuiTabs-scrollButtons': {
                  color: theme.palette.common.white,
                  '&.Mui-disabled': {
                    opacity: 0.3,
                  }
                },
              }}
            />
          </AppBoxComponent>
        </Stack>
      </Stack>


      {/* Main Content */}
      {shouldShowExploreBanner ? (
        <AppBoxComponent component="section" sx={LAYOUT_STYLES.exploreContentWrapper}>
          {!isVideoLoaded && <LoadingFallback fullPage />}
          <AppBoxComponent
            component="video"
            src={exploreBannerVideo}
            autoPlay
            loop
            muted
            playsInline
            controls={false}
            aria-hidden="true"
            onLoadedData={() => setIsVideoLoaded(true)}
            sx={{
              ...LAYOUT_STYLES.exploreBackgroundVideo,
              opacity: isVideoLoaded ? 1 : 0,
              transition: 'opacity 0.6s ease-in-out',
            }}
          />
          <PullToRefreshComponent
            onRefresh={handleRefresh}
            disabled={!(isNative() && location.pathname === '/my-investments')}
          >
            <Stack
              sx={{
                ...LAYOUT_STYLES.exploreContentOverlay,
                opacity: isVideoLoaded ? 1 : 0,
                transition: 'opacity 0.4s ease-in-out',
              }}
            >
              <Suspense fallback={<LoadingFallback fullPage />}>
                <Outlet />
              </Suspense>
            </Stack>
          </PullToRefreshComponent>
          {shouldShowServiceRequest && <ServiceRequestContainer />}
          <ApiHistoryDrawer showOnMobileOnly enabled={false} />
        </AppBoxComponent>
      ) : (
        <>
          <Stack sx={{
            ...LAYOUT_STYLES.mainContent,
            ...(isFullWidthContent ? { maxWidth: '100%', margin: 0 } : {})
          }}>
            {/* Breadcrumb Navigation - Hidden for top-level investment dashboard pages and Reports page */}
            {(location.pathname !== ROUTES.INVESTMENTS &&
              location.pathname !== ROUTES.FINANCIAL_OVERVIEW &&
              location.pathname !== ROUTES.REPORTS) && (
                <Box sx={{
                  px: breadcrumbPadding,
                  pr: showReturnLegend ? (
                    location.pathname.startsWith('/mutual-funds')
                      ? { xs: 2.5, sm: 4.5, md: 8.5 } // Higher offset for universe page
                      : { xs: 2.5, sm: 4.5, md: 4.5 } // offset for collection details
                  ) : breadcrumbPadding,
                  pt: 1,
                  pb: 0,
                  background: "#1D1D1D3D",
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                }}>
                  <BreadcrumbNavigation />
                  {showReturnLegend && <ReturnLegend />}
                </Box>
              )}

            <PullToRefreshComponent
              onRefresh={handleRefresh}
              disabled={!(isNative() && location.pathname === '/my-investments')}
            >
              <Suspense fallback={<LoadingFallback fullPage />}>
                <Outlet />
              </Suspense>
            </PullToRefreshComponent>
          </Stack>
          {shouldShowServiceRequest && <ServiceRequestContainer />}
          <ApiHistoryDrawer showOnMobileOnly enabled={false} />
        </>
      )}
      <ChooseExperienceModal
        open={showChooseExperience}
        onClose={handleChooseExperienceClose}
        onContinue={handleChooseExperienceContinue}
        userName={userName}
      />
      <DigioAgreementModal
        open={showAgreement}
        onClose={() => {
          setShowAgreement(false);
          hasAgreementBeenDismissed.current = true;
        }}
        userName={userName}
      />
      {/* Global Payment Verification Backdrop Dialog */}
      <Dialog
        open={isVerifying}
        disableEscapeKeyDown
        PaperProps={{
          sx: {
            background: "#13172A",
            borderRadius: "12px",
            p: 4,
            textAlign: "center",
            maxWidth: 400,
          }
        }}
      >
        <DialogContent sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
          <CircularProgress size={40} sx={{ color: "#46A8DC" }} />
          <Typography variant="h6" sx={{ color: "#fff", fontWeight: 600 }}>
            Verifying payment status
          </Typography>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>
            {verificationMessage || "Payment in progress. Please wait..."}
          </Typography>
          <AppButtonComponent
            variant="outlined"
            color="error"
            onClick={cancelVerification}
            sx={{ mt: 2, textTransform: "none" }}
          >
            Cancel Verification
          </AppButtonComponent>
        </DialogContent>
      </Dialog>
      {/* Embedded Razorpay Payment Iframe Dialog (Web only) */}
      <Dialog
        open={!!paymentUrl && !isNative()}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick') {
            cancelVerification();
          }
        }}
        disableEscapeKeyDown
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: "#13172A",
            borderRadius: "16px",
            height: "80vh",
            maxHeight: 700,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            position: "relative"
          }
        }}
      >
        <IconButton
          onClick={cancelVerification}
          sx={{
            position: 'absolute',
            right: 12,
            top: 12,
            color: '#fff',
            zIndex: 10
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ p: 0, height: "100%", display: "flex", flexDirection: "column" }}>
          {paymentUrl && (
            <iframe
              src={paymentUrl}
              title="Razorpay Subscription Payment"
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                background: "#fff"
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Onboarding Required Alert Dialog (Direct Plan only) */}
      <Dialog
        open={showOnboardingRequired}
        onClose={(e, reason) => {
          if (reason !== 'backdropClick') {
            setShowOnboardingRequired(false);
          }
        }}
        disableEscapeKeyDown
        sx={{ '& .MuiBackdrop-root': { backgroundColor: 'rgba(0, 0, 0, 0.65)' } }}
        PaperProps={{
          sx: {
            background: "#13172A",
            borderRadius: "16px",
            color: "#fff",
            maxWidth: 420,
            width: "100%",
            p: 3,
            textAlign: "center",
            position: 'relative'
          }
        }}
      >
        <IconButton
          onClick={() => setShowOnboardingRequired(false)}
          sx={{ position: 'absolute', right: 12, top: 12, color: 'rgba(255,255,255,0.6)' }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, pt: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Onboarding Required
          </Typography>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)", lineHeight: 1.5 }}>
            To activate your Direct Plan, please complete your onboarding journey (KYC details, bank account linking, fatca, nominee details, etc.) first.
          </Typography>
          <AppButtonComponent
            variant="contained"
            fullWidth
            onClick={() => {
              setShowOnboardingRequired(false);
              navigate(ROUTES.ONBOARDING_STATUS);
            }}
            sx={{ mt: 1, py: 1.25, borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}
          >
            Go to Onboarding Setup
          </AppButtonComponent>
        </DialogContent>
      </Dialog>
    </Stack>
  );
};

MainLayout.propTypes = {
  // No props for this component
};

export default memo(MainLayout);